import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  isISODateType,
  ISODateType,
  TypeGuard,
} from '@principle-theorem/shared';
import { isBoolean, isNumber, isString } from 'lodash';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import { SourceEntity } from '../../../source/source-entity';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { PATIENT_CLINICAL_NOTE_CUSTOM_MAPPING_TYPE } from '../../../destination/entities/consts';
import { EXISTING_PATIENT_DESTINATION_ENTITY } from '../../../destination/entities/existing-patient';
import { PATIENT_APPOINTMENT_RESOURCE_TYPE } from '../../../destination/entities/patient-appointments';
import { PATIENT_INTERACTION_RESOURCE_TYPE } from '../../../destination/entities/patient-interactions';
import { PATIENT_TREATMENT_PLAN_CUSTOM_MAPPING_TYPE } from '../../../destination/entities/patient-treatment-plans';
import { PATIENT_CLINICAL_CHART_DESTINATION_ENTITY } from '../../../exact/destination/entities/patient-clinical-charts';
import { PATIENT_DOCUMENTS_RESOURCE_TYPE } from '../../../exact/destination/entities/patient-documents';
import { PATIENT_INVOICE_DESTINATION_ENTITY } from '../../../exact/destination/entities/patient-invoices';
import { PATIENT_XRAYS_RESOURCE_TYPE } from '../../../exact/destination/entities/patient-xrays';

export const PATIENT_SOURCE_ENTITY = SourceEntity.init({
  metadata: {
    label: 'Patient List',
    description: ``,
    idPrefix: PATIENT_RESOURCE_TYPE,
    migrationType: SourceEntityMigrationType.Automatic,
  },
});

export interface IPracticeWorksPatient {
  id: string;
  userId?: string;
  personType: number;
  lastName?: string;
  firstName?: string;
  middleInitial?: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  postcode?: string;
  homePhone?: string;
  workPhone?: string;
  workPhone2?: string;
  email?: string;
  birthDate?: ISODateType;
  cellPhone?: string;
  sex: number;
  insuranceCard?: string;
  insuranceProvider?: string;
  medicareNumber?: string;
  onRecall: boolean;
  lastRecallDate?: string;
  recallCycleDays?: number;
  recallSchedUnits?: number;
  nextRecallDate?: string;
  createdDate?: ISODateType;
  preferredProviderId: number;
  noLongerPatient: boolean;
}

export function isPracticeWorksPatient(
  data: unknown,
  logError: boolean
): data is IPracticeWorksPatient {
  return TypeGuard.interface<IPracticeWorksPatient>(
    {
      id: isString,
      userId: TypeGuard.nilOr(isString),
      personType: isNumber,
      lastName: TypeGuard.nilOr(isString),
      firstName: TypeGuard.nilOr(isString),
      middleInitial: TypeGuard.nilOr(isString),
      address: TypeGuard.nilOr(isString),
      address2: TypeGuard.nilOr(isString),
      city: TypeGuard.nilOr(isString),
      state: TypeGuard.nilOr(isString),
      postcode: TypeGuard.nilOr(isString),
      homePhone: TypeGuard.nilOr(isString),
      workPhone: TypeGuard.nilOr(isString),
      workPhone2: TypeGuard.nilOr(isString),
      email: TypeGuard.nilOr(isString),
      birthDate: TypeGuard.nilOr(isISODateType),
      cellPhone: TypeGuard.nilOr(isString),
      sex: isNumber,
      insuranceCard: TypeGuard.nilOr(isString),
      insuranceProvider: TypeGuard.nilOr(isString),
      medicareNumber: TypeGuard.nilOr(isString),
      onRecall: isBoolean,
      lastRecallDate: TypeGuard.nilOr(isString),
      recallCycleDays: TypeGuard.nilOr(isNumber),
      recallSchedUnits: TypeGuard.nilOr(isNumber),
      nextRecallDate: TypeGuard.nilOr(isString),
      createdDate: TypeGuard.nilOr(isISODateType),
      preferredProviderId: isNumber,
      noLongerPatient: isBoolean,
    },
    logError
  )(data);
}

const PATIENT_SOURCE_QUERY = `
SELECT
  patient_data.*,
  patient_file.*,
  insurance_data.insurance_provider
FROM (
  SELECT
    person_id::TEXT AS id,
    NULLIF(user_id, '') AS user_id,
    person_type,
    NULLIF(last_name, '') AS last_name,
    NULLIF(first_name, '') AS first_name,
    NULLIF(mid_init, '') AS middle_initial,
    NULLIF(address, '') AS address,
    NULLIF(address_2, '') AS address_2,
    NULLIF(city, '') AS city,
    NULLIF(state, '') AS state,
    NULLIF(zip, '') AS postcode,
    NULLIF(home_phone, '') AS home_phone,
    NULLIF(work_phone_1, '') AS work_phone,
    NULLIF(work_phone_2, '') AS work_phone_2,
    NULLIF(email_address, '') AS email,
    NULLIF(birthdate, '') AS birth_date,
    NULLIF(cellphone, '') AS cell_phone,
    sex, -- 0 = male
    NULLIF(priminsid, '') AS insurance_card,
    prim_ins_plan_id AS insurance_plan_id,
    NULLIF(ausmedicarenum, '') AS medicare_number
  FROM _person_file_
) AS patient_data
INNER JOIN (
  SELECT
    person_id::TEXT,
    convert_to_boolean(on_recall) AS on_recall,
    last_recall_date,
    recall_cycle_days,
    recall_sched_units,
    next_recall_date, -- can be in the past
    NULLIF(date_first_entered, '') AS created_date,
    provider_emp_id AS preferred_provider_id,
    convert_to_boolean(nolongerpatient) AS no_longer_patient
  FROM _patient_file_
) AS patient_file
ON patient_data.id = patient_file.person_id
LEFT JOIN (
  SELECT
    ins_plan_id,
    employer_name AS insurance_provider
  FROM _ins_plan_info_
) AS insurance_data
ON patient_data.insurance_plan_id = insurance_data.ins_plan_id
WHERE convert_to_integer(patient_data.id) IS NOT NULL AND convert_to_integer(patient_data.id) > 0
`;

export type PracticeWorksPatient = ISourceEntityTypes<IPracticeWorksPatient>;

export class PatientSourceEntity extends BaseSourceEntity<PracticeWorksPatient> {
  sourceEntity = PATIENT_SOURCE_ENTITY;
  entityResourceType = PATIENT_RESOURCE_TYPE;
  sourceQuery = PATIENT_SOURCE_QUERY;
  verifySourceFn = isPracticeWorksPatient;

  migrationDestinations = [
    PATIENT_CLINICAL_CHART_DESTINATION_ENTITY.metadata.key,
    PATIENT_CLINICAL_NOTE_CUSTOM_MAPPING_TYPE,
    PATIENT_DOCUMENTS_RESOURCE_TYPE,
    PATIENT_INTERACTION_RESOURCE_TYPE,
    PATIENT_INVOICE_DESTINATION_ENTITY.metadata.key,
    PATIENT_TREATMENT_PLAN_CUSTOM_MAPPING_TYPE,
    PATIENT_XRAYS_RESOURCE_TYPE,
    PATIENT_RESOURCE_TYPE,
    PATIENT_APPOINTMENT_RESOURCE_TYPE,
    EXISTING_PATIENT_DESTINATION_ENTITY.metadata.key,
  ];

  getSourceRecordId(data: IPracticeWorksPatient): string {
    return data.id;
  }

  getSourceLabel(data: IPracticeWorksPatient): string {
    return `${data.id} ${data.firstName ?? ''} ${data.lastName ?? ''}`;
  }

  getFilterData(_: IPracticeWorksPatient): object {
    return {};
  }

  translate(_: IPracticeWorksPatient): object {
    return {};
  }
}
